import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { setPlayerProfile } from 'reducers/user';
import { errorToLogObject } from 'utils/apiErrors';
import { getAccessToken } from 'utils/auth';
import { clearBiometricCredentials } from 'utils/clearBiometricCredentials';
import { firePostRiskForDetailsChange } from 'utils/detailsChangeRisk';
import { APP_DOMAIN, BACKOFFICE_API_URL, SOCKET_SERVER_URL } from 'utils/urls';

export type PlayerProfile = {
  id: string;
  displayName: string;
  avatar: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  streetAddress: string;
  postalAddress: string;
  city: string;
  state: string;
  zipCode: string;
  countryCode: string;
  walletAddress: string;
  withdrawalWalletAddress: string;
  clubId: string;
  clubName: string;
  idDocType: string;
  idReason: string;
  totalFeesToday: number;
  idVerified: string;
  verifiedEmail: boolean;
  verifiedPhone: boolean;
  displayNameDateChanged: string;
  enableSecureLogin: boolean | null;
  // True when the account was created via a social provider (Google/Apple) and
  // therefore has no app-managed password — the Change Password UI is hidden.
  isSocialAuth: boolean;
};

// Matches Unity (AccountSettingsView.CheckIfSocialAuth): a Descope social login
// produces a loginId prefixed with the provider ('google-…' / 'apple-…').
const SOCIAL_LOGIN_PREFIXES = ['google-', 'apple-'];
export const isSocialAuthLoginIds = (loginIds: unknown): boolean =>
  Array.isArray(loginIds) &&
  loginIds.some(
    (id) =>
      typeof id === 'string' && SOCIAL_LOGIN_PREFIXES.some((p) => id.toLowerCase().startsWith(p))
  );

export type UpdateProfileResult = {
  success: boolean;
  message: string;
};

type FetchProfileOptions = {
  signal?: AbortSignal;
  silent?: boolean;
};

const emptyProfile: PlayerProfile = {
  id: '',
  displayName: '',
  avatar: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  streetAddress: '',
  postalAddress: '',
  city: '',
  state: '',
  zipCode: '',
  countryCode: 'US',
  walletAddress: '',
  withdrawalWalletAddress: '',
  clubId: '',
  clubName: '',
  idDocType: '',
  idReason: '',
  totalFeesToday: 0,
  idVerified: 'PENDING',
  verifiedEmail: false,
  verifiedPhone: false,
  displayNameDateChanged: '',
  enableSecureLogin: null,
  isSocialAuth: false
};

const addressFieldKeys = [
  'streetAddress',
  'postalAddress',
  'city',
  'state',
  'zipCode',
  'countryCode'
] as const;
type AddressPayload = Partial<Record<(typeof addressFieldKeys)[number], string | null>>;

/** Derive the engine REST API base URL from the socket URL */
function getEngineApiBase(): string {
  // SOCKET_SERVER_URL is like https://engine.hijack.poker or http://localhost:8002
  // The API is at the same host under /api
  let base = SOCKET_SERVER_URL || '';
  // Strip trailing slash
  base = base.replace(/\/+$/, '');
  // If it's a ws:// or wss:// URL, convert to http/https
  base = base.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
  return base;
}

function getDescopeToken(): string {
  return getAccessToken();
}

function getNullableAddressValue(value: string | undefined): string | null {
  const trimmedValue = String(value ?? '').trim();
  return trimmedValue === '' ? null : trimmedValue;
}

function getTrimmedValue(value: string | undefined): string {
  return String(value ?? '').trim();
}

function getAddressPayload(
  fields: Pick<
    PlayerProfile,
    'streetAddress' | 'postalAddress' | 'city' | 'state' | 'zipCode' | 'countryCode'
  >
): AddressPayload {
  return {
    streetAddress: getTrimmedValue(fields.streetAddress),
    postalAddress: getNullableAddressValue(fields.postalAddress),
    city: getTrimmedValue(fields.city),
    state: getTrimmedValue(fields.state),
    zipCode: getTrimmedValue(fields.zipCode),
    countryCode: getTrimmedValue(fields.countryCode)
  };
}

function getLocalAddressState(addressPayload: AddressPayload): Partial<PlayerProfile> {
  return Object.fromEntries(
    Object.entries(addressPayload).map(([field, value]) => [field, value ?? ''])
  ) as Partial<PlayerProfile>;
}

function getAddressResponseError(json: any, fallback: string): string {
  const errors = json?.errors || json?.data?.errors;

  if (errors && typeof errors === 'object') {
    const messages = Object.values(errors)
      .flat()
      .filter((message): message is string => typeof message === 'string');

    if (messages.length) return messages.join(' ');
  }

  return getResponseError(json, fallback);
}

function getResponseError(json: any, fallback: string): string {
  const errors = json?.errors || json?.data?.errors;
  if (errors && typeof errors === 'object') {
    const messages = Object.entries(errors)
      .flatMap(([field, value]) => {
        if (Array.isArray(value)) return value.map((message) => `${field}: ${message}`);
        if (typeof value === 'string') return [`${field}: ${value}`];
        return [];
      })
      .filter(Boolean);

    if (messages.length) return messages.join(' ');
  }

  return json?.data?.message || json?.data?.msg || json?.message || json?.error || fallback;
}

function getBackofficeUrl(): string {
  const domain = APP_DOMAIN || process.env.REACT_APP_DOMAIN || 'hijack.poker';
  return `https://backoffice.${domain}`;
}

const FETCH_TIMEOUT_MS = 10_000;

function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof (AbortSignal as any).timeout === 'function') {
    return (AbortSignal as any).timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

async function fetchJson(url: string, options?: RequestInit): Promise<any | null> {
  try {
    const res = await fetch(url, { ...options, signal: timeoutSignal(FETCH_TIMEOUT_MS) });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    // Includes AbortError on timeout — caller treats null as a missing payload.
    return null;
  }
}

function getClubName(clubId: string, clubsPayload: any): string {
  if (!clubId || !clubsPayload) return '';

  const club =
    clubsPayload[clubId] ||
    Object.values(clubsPayload).find((value: any) => {
      return value?.locationid === clubId || value?.locationId === clubId;
    });

  return (club as any)?.clubname || (club as any)?.clubName || '';
}

export type ProfileSource = 'cashier-deposit' | 'cashier-withdraw' | 'account-profile' | 'unknown';

export function usePlayerProfile(profileSource: ProfileSource = 'unknown') {
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const playerGUID = user.playerGUID;
  const playerId = user.playerId;
  const playerEmail = user.playerEmail;
  const [profile, setProfile] = useState<PlayerProfile>(emptyProfile);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      latestRequest.current += 1;
    };
  }, [playerGUID]);

  const fetchProfile = useCallback(
    async (options?: FetchProfileOptions) => {
      if (!playerGUID || !mounted.current || options?.signal?.aborted) return;
      const requestId = ++latestRequest.current;
      const isCurrent = () => mounted.current && requestId === latestRequest.current;
      const invalidate = () => {
        if (isCurrent()) {
          latestRequest.current += 1;
          setLoading(false);
        }
      };
      options?.signal?.addEventListener('abort', invalidate, { once: true });
      const silent = !!options?.silent;
      setLoading(!silent);
      setError(null);

      try {
        const base = getEngineApiBase();
        const jwt = getDescopeToken();
        const buildSearchUrl = (inDescope: boolean) =>
          `${base}/api/searchPlayer?GUID=${encodeURIComponent(playerGUID)}&inDescope=${
            inDescope ? 'true' : 'false'
          }&token=${encodeURIComponent(jwt)}&profileSource=${encodeURIComponent(profileSource)}`;

        const requestOptions: RequestInit = {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${jwt}`
          }
        };

        const [descopeRes, walletRes] = await Promise.all([
          fetch(buildSearchUrl(true), {
            ...requestOptions,
            signal: timeoutSignal(FETCH_TIMEOUT_MS)
          }),
          fetch(buildSearchUrl(false), {
            ...requestOptions,
            signal: timeoutSignal(FETCH_TIMEOUT_MS)
          })
        ]);

        if (!isCurrent()) return;
        if (!descopeRes.ok) throw new Error(`HTTP ${descopeRes.status}`);
        if (!walletRes.ok) throw new Error(`HTTP ${walletRes.status}`);

        const descopeJson = await descopeRes.json();
        const walletJson = await walletRes.json();
        if (!isCurrent()) return;
        const data = descopeJson?.data?.data?._list?.[0];
        const walletData = walletJson?.data?.data?._list?.[0];

        if (data) {
          const customAttributes = data.customAttributes || {};
          const clubId = customAttributes.club || data.club || '';
          const [feePayload, clubsPayload] = await Promise.all([
            fetchJson(
              `${getBackofficeUrl()}/api/players/get-todays-fee?playerGUID=${encodeURIComponent(
                playerGUID
              )}&token=${encodeURIComponent(jwt)}`,
              {
                headers: {
                  Accept: 'application/json',
                  Authorization: `Bearer ${jwt}`
                }
              }
            ),
            fetchJson(`https://media.${APP_DOMAIN || 'hijack.poker'}/meta/club.json`)
          ]);
          // Every asynchronous stage can outlive a newer opening or player identity.
          if (!isCurrent()) return;
          const totalFeesToday = Number(
            feePayload?.data?.[0]?.totalFee ??
              feePayload?.data?.totalFee ??
              data.totalFeesToday ??
              0
          );

          const idVerifiedStatus = customAttributes.idVerified || data.idVerified || 'PENDING';
          const firstName = customAttributes.firstName || data.firstName || '';
          const lastName = customAttributes.lastName || data.lastName || '';
          const phone = data.phone || '';
          const email = data.email || '';
          const streetAddress = customAttributes.streetAddress || data.streetAddress || '';
          const city = customAttributes.city || data.city || '';
          const stateValue = customAttributes.state || data.state || '';
          const zipCode = customAttributes.zipCode || data.zipCode || '';
          const countryCode = customAttributes.countryCode || data.countryCode || 'US';
          const walletAddress = walletData?.walletAddress || data.walletAddress || '';
          const withdrawalWalletAddress =
            walletData?.withdrawalWalletAddress || data.withdrawalWalletAddress || '';
          const verifiedEmail = !!customAttributes.verifiedEmail || !!data.verifiedEmail;
          const verifiedPhone = !!customAttributes.verifiedPhone || !!data.verifiedPhone;
          const rawEsl = customAttributes.enableSecureLogin;
          // Descope's Management API doesn't guarantee this custom attribute
          // comes back as a JS boolean — it's been observed as the numeric-
          // string '1' as well as true/'true'/1. Under-recognizing a truthy
          // value here reads as `false`, which the block below treats as a
          // genuine remote disable and wipes this device's biometric
          // credential — so this needs to accept every shape Descope might
          // hand back for "on", not just the ones a local mock would use.
          const enableSecureLogin: boolean | null =
            rawEsl === null || rawEsl === undefined
              ? null
              : rawEsl === true || rawEsl === 1 || rawEsl === '1' || rawEsl === 'true'
              ? true
              : false;

          // Persist the flag so LoginScreen can read it before the user authenticates.
          if (enableSecureLogin !== null) {
            try { localStorage.setItem('hj_biometric_enabled', String(enableSecureLogin)); } catch (_e) { /* ignore */ }
          }
          // If biometrics is explicitly disabled (including remotely — another
          // device, or a backoffice/admin revocation), clear whatever
          // credential THIS device holds so it can't still honor biometric
          // unlock. clearBiometricCredentials branches on native vs web
          // itself, so this one call covers both — a hand-rolled web-only
          // clear here used to leave a native device's keystore entry
          // untouched after a remote disable (EN-5415).
          if (enableSecureLogin === false) {
            void clearBiometricCredentials(email);
          }

          setProfile({
            id: data.ID || data.id || '',
            displayName:
              customAttributes.uniqueDisplayName ||
              data.uniqueDisplayName ||
              data.displayName ||
              '',
            avatar: data.avatar || data.playerAvatar || '',
            firstName,
            lastName,
            phone,
            email,
            streetAddress,
            postalAddress: customAttributes.postalAddress || data.postalAddress || '',
            city,
            state: stateValue,
            zipCode,
            countryCode,
            walletAddress,
            withdrawalWalletAddress,
            clubId,
            clubName: getClubName(clubId, clubsPayload),
            idDocType: customAttributes.idDocType || data.idDocType || '',
            idReason: customAttributes.idReason || data.idReason || '',
            totalFeesToday: Number.isFinite(totalFeesToday) ? totalFeesToday : 0,
            idVerified: idVerifiedStatus,
            verifiedEmail,
            verifiedPhone,
            displayNameDateChanged:
              customAttributes.displayNameDateCh ||
              customAttributes.display_name_changed_at ||
              data.displayNameDateCh ||
              data.display_name_changed_at ||
              '',
            enableSecureLogin,
            isSocialAuth: isSocialAuthLoginIds(data.loginIds)
          });

          // Mirror the profile fields the Redux slice owns. useCashierLimits
          // reads idVerified from Redux today; the remaining fields keep the
          // slice in sync with the local profile so Redux consumers don't
          // diverge from usePlayerProfile's view.
          dispatch(
            setPlayerProfile({
              firstName,
              lastName,
              phone,
              email,
              streetAddress,
              city,
              state: stateValue,
              zipCode,
              countryCode,
              walletAddress,
              withdrawalWalletAddress,
              idVerified: idVerifiedStatus,
              verifiedEmail,
              verifiedPhone,
              enableSecureLogin
            })
          );
        }
      } catch (e: any) {
        if (!isCurrent()) return;
        console.error('[usePlayerProfile] Error fetching profile:', errorToLogObject(e));
        setError(e.message || 'Failed to load profile');
      } finally {
        options?.signal?.removeEventListener('abort', invalidate);
        if (isCurrent()) {
          setLoading(false);
        }
      }
    },
    [playerGUID, dispatch, profileSource]
  );

  const updateProfile = useCallback(
    async (fields: Partial<PlayerProfile>) => {
      if (!playerGUID) return { success: false, message: 'Player GUID is required.' };
      setError(null);

      try {
        if (!playerId) {
          const message = 'Player ID is required to update profile.';
          setError(message);
          return { success: false, message };
        }

        const base = getEngineApiBase();
        const jwt = getDescopeToken();
        const url = `${base}/api/updatePlayerInfo`;
        const updateFields = { ...fields };
        const displayNameChangedAt = updateFields.displayName
          ? new Date().toISOString()
          : undefined;

        if (typeof updateFields.walletAddress === 'string') {
          updateFields.walletAddress = updateFields.walletAddress.replace(/\s+/g, '');
        }
        if (typeof updateFields.withdrawalWalletAddress === 'string') {
          updateFields.withdrawalWalletAddress = updateFields.withdrawalWalletAddress.replace(
            /\s+/g,
            ''
          );
        }
        const email = profile.email || playerEmail || '';

        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${jwt}`
          },
          body: JSON.stringify({
            GUID: playerGUID,
            ID: playerId,
            ...(email ? { email } : {}),
            token: jwt,
            jwt: jwt,
            ...updateFields,
            ...(displayNameChangedAt ? { display_name_changed_at: displayNameChangedAt } : {})
          })
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const json = await res.json();
        if (json?.data?.rc === '0') {
          // Update local state
          setProfile((prev) => ({
            ...prev,
            ...updateFields,
            ...(displayNameChangedAt ? { displayNameDateChanged: displayNameChangedAt } : {})
          }));
          // Mirror Unity: fire-and-forget PostRisk(DETAILS_CHANGE, AccountUpdate)
          // after a successful profile mutation (avatar / display name).
          // Sources: AvatarsView.cs:167, AccountsModalView.cs:247.
          firePostRiskForDetailsChange(playerGUID);
          return { success: true, message: json?.data?.message || 'Profile updated successfully.' };
        } else {
          throw new Error(json?.data?.message || json?.data?.msg || 'Update failed');
        }
      } catch (e: any) {
        console.error('[usePlayerProfile] Error updating profile:', errorToLogObject(e));
        const message = e.message || 'Failed to update';
        setError(message);
        return { success: false, message };
      }
    },
    [playerEmail, playerGUID, playerId, profile.email]
  );

  const updateAddress = useCallback(
    async (
      fields: Pick<
        PlayerProfile,
        'streetAddress' | 'postalAddress' | 'city' | 'state' | 'zipCode' | 'countryCode'
      >
    ) => {
      if (!playerGUID) return false;
      setError(null);

      try {
        const jwt = getDescopeToken();
        const addressPayload = getAddressPayload(fields);

        const res = await fetch(`${BACKOFFICE_API_URL}/players/update-address`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${jwt}`
          },
          body: JSON.stringify(addressPayload)
        });

        const json = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(getAddressResponseError(json, `Address update failed (${res.status})`));
        }

        if (json?.success === false) {
          throw new Error(getAddressResponseError(json, 'Address update failed'));
        }

        setProfile((prev) => ({ ...prev, ...getLocalAddressState(addressPayload) }));
        // Mirror Unity AddressModalView.cs:128 — fire-and-forget PostRisk after
        // a successful address update.
        firePostRiskForDetailsChange(playerGUID);
        return true;
      } catch (e: any) {
        console.error('[usePlayerProfile] Error updating address:', errorToLogObject(e));
        const message = e.message || 'Failed to update address';
        setError(message);
        return message;
      }
    },
    [playerGUID]
  );

  return { profile, loading, error, fetchProfile, updateProfile, updateAddress };
}
